import { useQuery, useLazyQuery } from "@apollo/client";
import { createContext, useContext, useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    GET_ALL_USERS,
    GET_LEFT_ROOMS,
    GET_ME,
    GET_TOTAL_UNREAD
} from "services/apollo/Queries";
import {
    setAllUsers,
    setConversationMap,
    setConversations,
    setPopup,
    setUser
} from "redux/messageSlice";
import { createConversationMap } from "Utils/Common";

const ConnectContext = createContext(null);

export const ConnectProvider = ({ children }) => {
    const dispatch = useDispatch();
    const [flaggedMessagePopup, setFlaggedMessagePopup] = useState(false);

    const { user: reduxUser } = useSelector((state) => state.message);

    // 1. Get current user
    const { data: meData, loading: meLoading } = useQuery(GET_ME, {
        fetchPolicy: "cache-first",
        onCompleted: (data) => {
            if (!data?.me) return;
            // ✅ Only update if different
            const prev = JSON.parse(localStorage.getItem("localUser") || "null");
            if (!prev || prev.id !== data.me.id) {
                localStorage.setItem("localUser", JSON.stringify(data.me));
            }
            if (!reduxUser || reduxUser.id !== data.me.id) {
                dispatch(setUser(data.me));
            }
        }
    });

    // ✅ Apollo first, localStorage fallback
    const user = meData?.me || JSON.parse(localStorage.getItem("localUser") || "null");

    // 2. Get conversations when user is available
    const { loading: roomsLoading } = useQuery(GET_LEFT_ROOMS, {
        skip: !user?.id,
        variables: { userId: user?.id },
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            if (!data?.rooms) return;
            dispatch(setConversations(data.rooms));
            dispatch(setConversationMap(createConversationMap(data.rooms)));
        },
        onError: (err) => console.error("Error fetching rooms:", err)
    });

    // 3. Fetch other users lazily
    const [fetchUsers] = useLazyQuery(GET_ALL_USERS, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            if (!data?.users) return;
            dispatch(
                setAllUsers(data.users.filter((u) => u.role !== "Public User"))
            );
        }
    });

    // 4. Fetch unread counter lazily
    const [fetchUnread] = useLazyQuery(GET_TOTAL_UNREAD, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            // console.log("🚀 ~ ConnectProvider ~ GET_TOTAL_UNREAD:", data)
            dispatch(setPopup({ key: 'total_unread', value: data.total_unread }));
        }
    });

    // ✅ Run only when company_id changes
    useEffect(() => {
        if (user?.company_id) {
            fetchUsers({ variables: { company_id: user.company_id } });
            fetchUnread();
        }
    }, [user?.company_id]);

    // 5. Derived loader
    const connectLoader = meLoading || roomsLoading;

    const value = useMemo(
        () => ({
            connectLoader, user, flaggedMessagePopup, setFlaggedMessagePopup
        }),
        [connectLoader, user, flaggedMessagePopup]
    );

    return (
        <ConnectContext.Provider value={value}>
            {children}
        </ConnectContext.Provider>
    );
};

export const useConnect = () => {
    const context = useContext(ConnectContext);
    if (!context) {
        throw new Error("useConnect must be used within a ConnectProvider");
    }
    return context;
};
